import { APP_ROUTER, cityName, imageTransform, provinceName } from '@qctsw/common'
import { contentRemoveTags, isClient, isValidKey, timeFormat, timeFrom } from '@qctsw/utils'
import ComplaintAuthor from '../components/ComplaintAuthor.vue'
import Issue from '../components/Issue.vue'
import { logicUserComplaintDetail } from '~/logic/user/complaint'
import { getCarInsuranceCodeText, useGetRouteParam } from '~/shared/function'
import { NuxtLink } from '#components'
import type { ComplaintDetailResponse } from '~/server/types'
import { CAR_INSURANCE_CODE, CAR_SYSTEM_LEVEL, ISSUE_TYPE_CODE_ENUM } from '~/shared/constants'
import type { ComplaintFeedback, ManufacturerResult } from '~/pages/user/hooks/complaint'
import { DEFAULT_KEYWORDS, SITE_NAME } from '~/shared/site'

export function useComplaintDetail() {
  const { changeMeta } = useSite()
  const { setShareBase } = useShare()

  const id = useGetRouteParam('id', '')
  const {
    data,
    state,
    getData,
  } = logicUserComplaintDetail()

  async function initHandler() {
    await getData(id.value)

    if (!data.value)
      return
    // meta
    changeMeta({
      title: `${data.value.title} - ${SITE_NAME}`,
      description: contentRemoveTags(data.value.baseInfo.content).slice(0, 200),
      keywords: [...DEFAULT_KEYWORDS, data.value?.brandName, data.value?.seriesName, data.value?.modelName, data.value.dealerName, data.value.title.replace(/，/g, ',')].filter(Boolean) as string[],
    })

    if (isClient) {
      // share
      setShareBase({
        url: window.location.href,
        title: data.value.title,
        content: contentRemoveTags(data.value.baseInfo.content).slice(0, 200),
        images: data.value.imgLs.filter(v => !v.imgUrl.match(/^data:.*;base64.*/)).map(v => imageTransform(v.imgUrl)),
      })
    }
  }

  // 最终结果id
  const resultId = ref<number | null>(null)
  // 厂商结果
  const manufacturerResult = computed(() => {
    const res = data.value
    if (!res)
      return []

    const newMessageList: ManufacturerResult[] = []
    res.defaultLs.forEach((v) => {
      newMessageList.push({
        ...v,
        isDefault: true,
      })
    })
    res.resultLs.forEach((v: any) => {
      if (v.disposeTypeCode === 1)
        resultId.value = v.id // 最终结果id

      newMessageList.push({
        ...v,
        isDefault: false,
      })
    })

    return newMessageList
  })
  // 投诉反馈
  const feedbackList = computed<ComplaintFeedback[]>(() => {
    const res = data.value
    if (!res)
      return []
    const feedbackList = res.feedbackLs.map((v) => {
      let replyList: ComplaintFeedback['replyList'] = []
      if (v.replyLs && v.replyLs.length) {
        replyList = v.replyLs.map(item => ({
          createTime: timeFrom(v.createTime),
          id: item.id,
          feedbackContent: item.replyContent,
          userTypeCode: item.userTypeCode,
        }))
      }
      return {
        createTime: timeFrom(v.createTime),
        id: v.id,
        feedbackContent: v.feedbackContent,
        userTypeCode: v.userTypeCode,
        replyList,
      }
    })
    return feedbackList
  })

  return {
    id,
    detailData: data,
    state,
    tableData: complaintDetailTableData(data),
    resultId,
    manufacturerResult, // 厂商结果
    feedbackList, // 投诉反馈
    initHandler,
  }
}

function complaintDetailTableData(data: Ref<ComplaintDetailResponse | null>) {
  return computed(() => {
    const res = data.value
    if (!res)
      return []

    let grade = ''
    if (res.grade && isValidKey(res.grade, CAR_SYSTEM_LEVEL))
      grade = CAR_SYSTEM_LEVEL[res.grade]

    const { portalUserProvinceId: provinceId, portalUserCityId: cityId } = res

    const appeal = res.issueLs.filter(v => v.issueTypeCode === ISSUE_TYPE_CODE_ENUM.appeal)
    const issue = res.issueLs.filter(v => v.issueTypeCode !== ISSUE_TYPE_CODE_ENUM.appeal)
    return [
      {
        label: '投诉人',
        content: () => [
          h(ComplaintAuthor, { name: res.carUserName, sex: res.userSex }),
        ],
        // content: h(ComplaintAuthor, { name: res.carUserName, sex: res.userSex }),
        isShow: !!res.carUserName,
        isComponent: true,
      },
      {
        label: '投诉品牌',
        isComponent: true,
        content: () => [
          h(NuxtLink, {
            to: APP_ROUTER.complaintSearch({ carBrandId: res.carBrandId, carBrandName: res.brandName }),
            class: 'hover:text-blue-5',
          }, { default: () => res.brandName ?? res.vendorName }),
        ],
        isShow: !!res.brandName,
      },
      {
        label: '投诉编号',
        tips: { className: 'text-red-5', content: '请牢记此投诉编号' },
        content: `QT${res.id}`,
        isShow: !!res.id,
      },
      {
        label: '投诉车型',
        isComponent: true,
        content: () => [
          h('div', null, [
            h(NuxtLink, {
              to: APP_ROUTER.complaintSearch({ carBrandId: res.carBrandId, carSeriesId: res.carSeriesId, carBrandName: res.brandName, carSeriesName: res.seriesName }),
              class: 'hover:text-blue-5',
            }, { default: () => res.seriesName }),
            h(NuxtLink, {
              to: APP_ROUTER.complaintSearch({ carBrandId: res.carBrandId, carSeriesId: res.carSeriesId, modelId: res.carModelId, carBrandName: res.brandName, carSeriesName: res.seriesName }),
              class: 'hover:text-blue-5',
            }, { default: () => res.modelName }),
          ]),
        ],
        isShow: !![res.seriesName, res.modelName].join(''),
      },
      {
        label: '投诉时间',
        content: res.createTime,
        isShow: !!res.createTime,
      },
      {
        label: '所属类型',
        content: grade,
        isShow: !!grade,
      },
      {
        label: '投诉地区',
        isComponent: true,
        content: () => [
          h('div', {}, [
            cityName(cityId, '', true) !== provinceName(provinceId, '', true)
            && h(NuxtLink, {
              to: APP_ROUTER.complaintSearch({ provinceId }),
              class: 'hover:text-blue-5',
            }, { default: () => provinceName(provinceId, '', true) }),
            h(NuxtLink, {
              to: APP_ROUTER.complaintSearch({ provinceId, cityId }),
              class: 'hover:text-blue-5',
            }, { default: () => cityName(cityId, '', true) }),
          ]),
        ],
        isShow: !![provinceId, cityId].filter(v => v).join(''),
      },
      {
        label: '4S店名称',
        content: res.dealerName,
        isShow: !!res.dealerName,
      },
      {
        label: '里程数(KM)',
        content: (res.baseInfo.travelKm ?? 0),
        isShow: !!res.baseInfo.travelKm,
      },
      {
        label: '购车时间',
        content: timeFormat(res.baseInfo.shoppingTime, 'YYYY-MM-DD'),
        isShow: !!res.baseInfo.shoppingTime,
      },
      {
        label: '车保状态',
        isComponent: true,
        content: () => [
          h(NuxtLink, {
            to: APP_ROUTER.complaintSearch({ insuranceCode: res.insuranceCode }),
            class: 'hover:text-blue-5',
          }, { default: () => getCarInsuranceCodeText(res.insuranceCode) }),
        ],
        isShow: true,
      },

      {
        label: '涉诉金额',
        content: () => `${res.baseInfo.complainMoney}万元`,
        isComponent: true,
        isShow: res.baseInfo.complainMoney > 0,
      },

      {
        label: '诉求',
        content: () => [h(Issue, { type: 'appeal', list: appeal })],
        isComponent: true,
        isShow: !!appeal.length,
      },
      {
        label: '投诉问题',
        style: { width: '100%' },
        isShow: !!issue.length,
        isComponent: true,
        content: () => [h(Issue, { list: issue })],
      },
    ]
  })
}
